<template>
    <div>
        <!-- Anchor element for accurate cursor positioning -->
        <div ref="menuAnchor" style="position: fixed; width: 0; height: 0; pointer-events: none; z-index: 9999;"></div>

        <v-menu v-model="show" :activator="menuActivator" :close-on-content-click="true" location="bottom start"
            :offset="[5, 0]">
            <v-list density="compact" max-height="200" min-width="150" elevation="2">
                <v-list-item v-if="filteredItems.length === 0" disabled>
                    <v-list-item-title class="text-caption italic">No matches foundation</v-list-item-title>
                </v-list-item>
                <v-list-item v-for="(item, index) in filteredItems" :key="index" @click="selectItem(item)"
                    :value="item">
                    <v-list-item-title class="text-caption">{{ item }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
export default {
    name: 'KeywordAutocomplete',
    props: {
        items: {
            type: Array,
            required: true
        },
        triggerChar: {
            type: String,
            default: '['
        }
    },
    emits: ['select'],
    data() {
        return {
            show: false,
            menuActivator: null,
            context: null,
            searchQuery: '',
            startOffset: -1
        }
    },
    computed: {
        filteredItems() {
            if (!this.searchQuery) return this.items;
            const query = this.searchQuery.toLowerCase();
            return this.items.filter(item =>
                item.toLowerCase().includes(query)
            );
        }
    },
    methods: {
        /**
         * Opens the menu at the current cursor position of the input element.
         * Useful for keyup events.
         * @param {HTMLElement} inputElement - The input or textarea element
         * @param {Object} context - Any data you want passed back on select
         */
        openAtCursor(inputElement, context = null) {
            this.context = context;
            const coords = this.getCursorCoordinates(inputElement);
            const anchor = this.$refs.menuAnchor;

            if (anchor && coords) {
                anchor.style.left = `${coords.x}px`;
                anchor.style.top = `${coords.y + coords.height}px`;
                this.menuActivator = anchor;
                this.show = true;
            }
        },

        /**
         * Opens the menu relative to a specific element (button click).
         * @param {HTMLElement} element - The activator element
         * @param {Object} context - Any data you want passed back on select
         */
        openAtElement(element, context = null) {
            this.context = context;
            this.menuActivator = element;
            this.show = true;
            this.searchQuery = '';
            this.startOffset = -1;
        },

        /**
         * Convenience method to handle keyup events automatically.
         * Checks if the key matches the triggerChar.
         * @param {KeyboardEvent} event 
         * @param {Object} context 
         */
        handleKeyUp(event, context = null) {
            const input = event.target;
            const cursor = input.selectionStart;
            const text = input.value;

            // Find the last occurrence of triggerChar before or at the cursor
            const lastTrigger = text.lastIndexOf(this.triggerChar, cursor - 1);

            if (lastTrigger !== -1) {
                const textBetween = text.substring(lastTrigger + 1, cursor);

                // If there's a space, it's not a search anymore
                if (textBetween.includes(' ')) {
                    this.show = false;
                    return;
                }

                this.startOffset = lastTrigger;
                this.searchQuery = textBetween;

                if (!this.show) {
                    this.openAtCursor(input, context);
                }
            } else {
                this.show = false;
            }
        },

        selectItem(item) {
            this.$emit('select', {
                item,
                context: this.context,
                range: this.startOffset !== -1 ? {
                    start: this.startOffset,
                    end: this.startOffset + this.searchQuery.length + 1 // +1 for triggerChar
                } : null
            });
            this.show = false;
            this.searchQuery = '';
            this.startOffset = -1;
        },

        getCursorCoordinates(element) {
            if (!element) return null;

            const { selectionEnd, value } = element;
            const style = window.getComputedStyle(element);

            // Create mirror element
            const div = document.createElement('div');

            // Styles to copy to mirror exact layout
            const properties = [
                'direction', 'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
                'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderStyle',
                'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
                'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'fontSizeAdjust',
                'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent',
                'textDecoration', 'letterSpacing', 'wordSpacing', 'tabSize', 'MozTabSize',
                'whiteSpace', 'wordBreak', 'wordWrap'
            ];

            properties.forEach(prop => {
                div.style[prop] = style[prop];
            });

            // Mirror specific styles to ensure it doesn't affect page but accurately calculates
            div.style.position = 'absolute';
            div.style.top = '0px';
            div.style.left = '-9999px';
            div.style.visibility = 'hidden';
            div.style.zIndex = '-1000';

            // Set content up to the cursor
            div.textContent = value.substring(0, selectionEnd);

            // Marker for cursor
            const span = document.createElement('span');
            span.textContent = '|';
            div.appendChild(span);

            document.body.appendChild(div);

            // Calculate coordinates
            const relativeX = span.offsetLeft;
            const relativeY = span.offsetTop;
            const lineHeight = span.offsetHeight;

            document.body.removeChild(div);

            // Adjust relative to viewport
            const rect = element.getBoundingClientRect();
            const borderLeft = parseFloat(style.borderLeftWidth) || 0;
            const borderTop = parseFloat(style.borderTopWidth) || 0;

            const x = rect.left + borderLeft + relativeX - element.scrollLeft;
            const y = rect.top + borderTop + relativeY - element.scrollTop;

            return { x, y, height: lineHeight };
        }
    }
}
</script>
